// Datenschutzerklärung — public page accessible without auth.
// Reflects the actual data processing of the 4M CRM platform: client data,
// property data, document signing, SMS/email reminders, AI assistant.

import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { COMPANY } from '@4m/shared';
import { formatDate } from '@/lib/formatLocale';

export const Route = createFileRoute('/datenschutz')({
  component: DatenschutzPage,
});

function DatenschutzPage() {
  return (
    <main className="min-h-screen bg-paper text-ink">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-3xl items-center gap-4 px-6 py-5">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-medium uppercase text-ink-3 tracking-[0.1em] hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Zur Startseite
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-6 py-12 prose-styles">
        <h1 className="font-display text-4xl tracking-tight text-ink">Datenschutzerklärung</h1>
        <p className="mt-2 text-sm text-ink-3">
          Information gemäß Art. 13 DSGVO und § 25 MedienG.
        </p>

        <section className="mt-10 space-y-3 text-sm leading-relaxed text-ink-2">
          <h2 className="font-display text-2xl text-ink">1. Verantwortliche Stelle</h2>
          <p>
            <strong>{COMPANY.legalName}</strong>
            <br />
            {COMPANY.address.formatted}
            <br />
            Tel.: {COMPANY.contact.phone} · E-Mail:{' '}
            <a className="underline-offset-2 hover:underline" href={`mailto:${COMPANY.contact.email}`}>
              {COMPANY.contact.email}
            </a>
            <br />
            FB Nr. {COMPANY.legal.firmenbuchNr} · UID {COMPANY.legal.uid}
          </p>
        </section>

        <section className="mt-10 space-y-3 text-sm leading-relaxed text-ink-2">
          <h2 className="font-display text-2xl text-ink">2. Welche Daten wir verarbeiten</h2>
          <p>
            Im Rahmen unserer Tätigkeit als Immobilienmakler verarbeiten wir folgende
            Datenkategorien:
          </p>
          <ul className="ms-5 list-disc space-y-1">
            <li>
              <strong>Stammdaten:</strong> Vor- und Nachname, Geburtsdatum, Anschrift,
              Staatsbürgerschaft.
            </li>
            <li>
              <strong>Kontaktdaten:</strong> Telefon-/Mobilnummer, E-Mail-Adresse.
            </li>
            <li>
              <strong>Vertragsdaten:</strong> Vermittlungsaufträge, Besichtigungsscheine,
              Kaufanbote, samt Unterschriften (auch elektronischen).
            </li>
            <li>
              <strong>Objektdaten:</strong> Adresse der Immobilie, Eigentumsverhältnisse,
              Energieausweis, Marktwert, Fotos.
            </li>
            <li>
              <strong>Kommunikationsdaten:</strong> SMS-Verkehr (versendet via Twilio),
              E-Mail-Verkehr (versendet via Resend), App-interner Chat zwischen
              Mitarbeiter:innen.
            </li>
            <li>
              <strong>Audit-Log:</strong> Wer hat wann welche Aktion in der CRM-Plattform
              ausgeführt.
            </li>
          </ul>
        </section>

        <section className="mt-10 space-y-3 text-sm leading-relaxed text-ink-2">
          <h2 className="font-display text-2xl text-ink">3. Zweck der Verarbeitung</h2>
          <p>
            Vertragsanbahnung, Vertragsabwicklung, Maklertätigkeit gemäß Maklergesetz,
            Erfüllung gesetzlicher Aufbewahrungs- und Meldepflichten (UGB, BAO, GwG-WAG),
            Versand von Termin-Erinnerungen und Vertragsdokumenten, Pflege unserer
            Kundenbeziehung.
          </p>
          <p>
            Rechtsgrundlage: Art. 6 Abs. 1 lit. b (Vertrag), lit. c (Rechtspflicht),
            lit. f (berechtigtes Interesse) DSGVO.
          </p>
        </section>

        <section className="mt-10 space-y-3 text-sm leading-relaxed text-ink-2">
          <h2 className="font-display text-2xl text-ink">4. Datenresidenz und Auftragsverarbeiter</h2>
          <p>
            Ihre Daten werden in der Europäischen Union verarbeitet und gespeichert
            (Cloudflare D1, Region EU). Wir setzen folgende Auftragsverarbeiter im Sinne
            des Art. 28 DSGVO ein:
          </p>
          <ul className="ms-5 list-disc space-y-1">
            <li>
              <strong>Cloudflare, Inc.</strong> (USA, EU-Region) — Hosting der CRM-Plattform,
              Datenbank, Datei-Speicher. EU-Cloud-Code-of-Conduct, SCC und DPF zertifiziert.
            </li>
            <li>
              <strong>Twilio Inc.</strong> (USA) — SMS-Versand. SCC, DPF.
            </li>
            <li>
              <strong>Resend (Poseidon Labs Inc.)</strong> (USA) — E-Mail-Versand. SCC.
            </li>
            <li>
              <strong>Anthropic PBC</strong> (USA) — KI-Assistent „Zoki". Nur anonymisierte
              Anfragen ohne PII.
            </li>
          </ul>
        </section>

        <section className="mt-10 space-y-3 text-sm leading-relaxed text-ink-2">
          <h2 className="font-display text-2xl text-ink">5. Speicherdauer</h2>
          <p>
            Vertragsdaten werden gemäß § 132 BAO und § 212 UGB sieben Jahre lang
            aufbewahrt. Daten ohne Vertragsbezug (z. B. Interessentenanfragen ohne
            Folgegeschäft) werden nach drei Jahren gelöscht. Interne Audit-Logs werden für
            zehn Jahre vorgehalten.
          </p>
        </section>

        <section className="mt-10 space-y-3 text-sm leading-relaxed text-ink-2">
          <h2 className="font-display text-2xl text-ink">6. Ihre Rechte</h2>
          <p>
            Sie haben jederzeit Recht auf Auskunft, Berichtigung, Löschung,
            Einschränkung, Datenübertragbarkeit, Widerruf einer erteilten Einwilligung
            und Widerspruch. Anfragen richten Sie bitte schriftlich an{' '}
            <a className="underline-offset-2 hover:underline" href={`mailto:${COMPANY.contact.email}`}>
              {COMPANY.contact.email}
            </a>
            .
          </p>
          <p>
            Sie haben außerdem das Recht auf Beschwerde bei der österreichischen
            Datenschutzbehörde (
            <a
              className="underline-offset-2 hover:underline"
              href="https://www.dsb.gv.at"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.dsb.gv.at
            </a>
            ).
          </p>
        </section>

        <section className="mt-10 space-y-3 text-sm leading-relaxed text-ink-2">
          <h2 className="font-display text-2xl text-ink">7. Cookies und Tracking</h2>
          <p>
            Auf{' '}
            <a className="underline-offset-2 hover:underline" href={COMPANY.contact.appUrl}>
              {COMPANY.contact.appUrl.replace(/^https?:\/\//, '')}
            </a>{' '}
            verwenden wir ausschließlich technisch notwendige Cookies (Session-Token,
            Sprache, Theme-Einstellung). Es findet kein Tracking, kein Profiling und keine
            Weitergabe an Werbenetzwerke statt.
          </p>
        </section>

        <p className="mt-12 text-xs text-ink-3">
          Stand: {formatDate(new Date(COMPANY.version))}
        </p>
      </article>

      <footer className="border-t border-line py-6">
        <div className="mx-auto max-w-3xl px-6 text-center text-xs text-ink-3">
          © {new Date().getFullYear()} {COMPANY.legalName}
        </div>
      </footer>
    </main>
  );
}
